/**
 * "Was this helpful?" under a Chat with Boris answer — the same shape as the TA board's
 * DraftFeedback, with two deliberate differences for an employee audience:
 *
 *  - the comment on a 👎 is OPTIONAL. A required box in front of a thumbs-down mostly produces no
 *    vote at all, and the question/answer pair stored with it is diagnosable on its own;
 *  - the control says, before the click, that rating saves the question and the answer. The chat
 *    itself stores nothing, so this is the one moment an employee's question is kept, and it should
 *    not be a surprise (proxy-server/lib/borisFeedback.js).
 *
 * A vote can be CHANGED after it is given (CodeRabbit, PR #494): one vote per employee per answer
 * is stored and a second one overwrites it (proxy-server/lib/borisFeedback.js), so an accidental
 * click must be correctable from here too.
 *
 * The analytics event carries the vote, `covered`, whether a comment was written and whether it
 * revised an earlier vote — never the comment, the question or the answer. A failed save still says thank you: this is telemetry, and a
 * lost vote must not look like a broken button.
 */
import { useState } from 'react';
import { Button } from '@finn-auto/design-system/atoms/button';
import { Input } from '@finn-auto/design-system/atoms/input';
import { logAnalyticsEvent } from '../../../analytics/nativeAnalytics';
import { rateBorisAnswer, type PolicyChatCitation } from '../services/policyChatApi';

interface Props {
  answerId: string;
  question: string;
  answer: string;
  citations: PolicyChatCitation[];
  covered?: boolean;
}

export function BorisAnswerFeedback({ answerId, question, answer, citations, covered }: Props) {
  const [state, setState] = useState<'idle' | 'commenting' | 'done'>('idle');
  const [comment, setComment] = useState('');
  const [busy, setBusy] = useState(false);
  const [voted, setVoted] = useState(false);

  const send = async (vote: 'up' | 'down', text = '') => {
    setBusy(true);
    try {
      await rateBorisAnswer({
        answerId,
        vote,
        comment: text.trim(),
        question,
        answer,
        sources: citations.map((c) => c.title),
        covered,
      });
    } catch {
      // Telemetry: thank them anyway.
    } finally {
      void logAnalyticsEvent('boris_answer_rated', {
        vote,
        covered: covered ?? null,
        withComment: Boolean(text.trim()),
        revised: voted,
      });
      setBusy(false);
      setVoted(true);
      setState('done');
    }
  };

  if (state === 'done') {
    return (
      <div className="mt-1 flex items-center gap-1 text-xs text-gray-400">
        <span>Thanks — noted.</span>
        <Button
          size="sm"
          variant="ghost"
          onClick={() => {
            setComment('');
            setState('idle');
          }}
        >
          Change rating
        </Button>
      </div>
    );
  }

  if (state === 'commenting') {
    return (
      <div className="mt-2 flex flex-wrap items-end gap-2">
        <div className="w-64">
          <Input
            label="What was wrong? (optional)"
            value={comment}
            maxLength={600}
            onChange={(e) => setComment((e.target as HTMLInputElement).value)}
            disabled={busy}
          />
        </div>
        <Button size="sm" variant="outline" disabled={busy} onClick={() => void send('down', comment)}>
          Send feedback
        </Button>
        <Button size="sm" variant="ghost" disabled={busy} onClick={() => setState('idle')}>
          Cancel
        </Button>
      </div>
    );
  }

  return (
    <div className="mt-1 flex items-center gap-1 text-xs text-gray-400">
      <span>Helpful?</span>
      <Button
        size="sm"
        variant="ghost"
        aria-label="This answer was helpful"
        disabled={busy}
        onClick={() => void send('up')}
      >
        👍
      </Button>
      <Button
        size="sm"
        variant="ghost"
        aria-label="This answer was not helpful"
        disabled={busy}
        onClick={() => setState('commenting')}
      >
        👎
      </Button>
      <span className="ml-1">Rating saves this question and answer for the People team.</span>
    </div>
  );
}
